import { times as _times, shuffle as _shuffle } from 'lodash-es'
import {
  Factory,
  Model,
  Response,
  type Collection,
  type ModelInstance,
} from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import type { ShareReportAttributes } from '@/composables/api/ShareReport.js'
import {
  rand,
  randPastDate,
  randNumber,
  randChanceBoolean,
  randEmail,
  randFirstName,
  randLastName,
  randParagraph,
  randTextRange,
} from '@ngneat/falso'
import {
  sortable,
  pageable,
  filterable,
  createErrorResponse,
} from '@@/api/mock-server/utils.js'
import type { MirageServer, MockServer } from '@@/api/mock-server/server'
import { ShareReportStatus } from '@/models/reports/shareReport.js'
import { getTodayDateTimeISO } from '@/utils/date/getter.js'

export const model = Model as ModelDefinition<ShareReportAttributes>

export const factory = Factory.extend({
  created_at() {
    return randPastDate().toISOString()
  },
  updated_at() {
    return randPastDate().toISOString()
  },
  created_by_name() {
    return `${randFirstName()} ${randLastName()}`
  },
  is_own() {
    return randChanceBoolean({ chanceTrue: 0.5 })
  },
  report_type() {
    return 'invoice_v2'
  },
  report_data() {
    return null
  },
  status() {
    if (randChanceBoolean({ chanceTrue: 0.7 })) {
      return ShareReportStatus.Sent
    } else if (randChanceBoolean({ chanceTrue: 0.1 })) {
      return ShareReportStatus.Pending
    }
    return ShareReportStatus.Failed
  },
  error_message() {
    return null
  },
  sent_at(this: ShareReportAttributes) {
    return this.status === ShareReportStatus.Sent
      ? randPastDate().toISOString()
      : null
  },
  report_formats() {
    const options: ('CSV' | 'XLSX' | 'PDF')[] = ['CSV', 'XLSX', 'PDF']
    const removeCount = randNumber({ min: 0, max: options.length - 1 })

    // Keep (3 - removeCount) items from shuffled array, ensuring at least 1 format
    const shuffled = _shuffle(options)
    return shuffled.slice(0, options.length - removeCount)
  },
  from_email() {
    return randEmail({
      firstName: randFirstName(),
      provider: rand(['example.org', 'test.com', 'demo.net']),
    })
  },
  to_emails() {
    return _times(randNumber({ min: 1, max: 5 }), () =>
      randEmail({
        firstName: randFirstName(),
        provider: 'some-bath-clinic',
      }),
    )
  },
  subject() {
    return randTextRange({
      min: 10,
      max: 30,
    }).replace(/\.$/, '')
  },
  body() {
    return randParagraph()
  },
})

export function routes(server: MockServer) {
  server.get(
    '/reports_v2/share',
    function (this: MirageServer, schema, request) {
      const allShareReports = schema.all('shareReport') as Collection<
        ModelInstance<ShareReportAttributes>
      >

      // 2) Filter using the established utility
      const filtered = filterable(allShareReports, request, {
        search: (item, value) => {
          const searchTerm = value.toLowerCase()
          const emails = item.to_emails || []
          return emails.some((email: string) =>
            email.toLowerCase().includes(searchTerm),
          )
        },
        status: (item, value) => (item.status ? item.status === value : true),
      })

      // 2) Sort
      const sorted = sortable(filtered, request)

      // 3) Paginate
      const { data, meta } = pageable(sorted, request)

      return {
        data: data.models.map((report) => {
          return {
            id: report.id,
            type: 'ShareReport',
            attributes: report.attrs,
          }
        }),
        meta: {
          pagination: meta,
        },
      }
    },
  )

  server.get('/reports_v2/share/:id', (schema, request) => {
    const id = request.params.id
    const shareReport = schema.find(
      'shareReport',
      id,
    ) as ModelInstance<ShareReportAttributes> | null

    if (!shareReport) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `ShareReport with id ${id} not found` }],
      })
    }

    return {
      data: {
        id: shareReport.id,
        type: 'ShareReport',
        attributes: shareReport.attrs,
      },
    }
  })

  server.post('/reports_v2/share', async (schema, request) => {
    const requestBody = JSON.parse(request.requestBody)
    const { attributes } = requestBody.data
    const createdShareReport = await schema.create('shareReport', attributes)

    return {
      data: {
        id: createdShareReport.id,
        type: 'ShareReport',
        attributes: createdShareReport.attrs,
      },
    }
  })

  server.patch('/reports_v2/share/:id', async (schema, request) => {
    const id = request.params.id
    const requestBody = JSON.parse(request.requestBody)
    const shareReport = schema.find(
      'shareReport',
      id,
    ) as ModelInstance<ShareReportAttributes> | null

    if (!shareReport) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `ShareReport with id ${id} not found` }],
      })
    }

    if (!requestBody?.data?.attributes) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "attributes" in request body' }],
      })
    }

    const newAttributes = requestBody.data.attributes
    await shareReport.update({
      ...shareReport.attrs,
      ...newAttributes,
    })

    return {
      data: {
        id: shareReport.id,
        type: 'ShareReport',
        attributes: shareReport.attrs,
      },
    }
  })

  server.delete('/reports_v2/share/:id', async (schema, request) => {
    const id = request.params.id
    const shareReport = schema.find(
      'shareReport',
      id,
    ) as ModelInstance<ShareReportAttributes> | null

    if (!shareReport) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `ShareReport with id ${id} not found` }],
      })
    }

    await shareReport.destroy()

    return new Response(204, {}, '')
  })

  server.post('/reports_v2/share/:id/resend', async (schema, request) => {
    const id = request.params.id
    const shareReport = schema.find(
      'shareReport',
      id,
    ) as ModelInstance<ShareReportAttributes> | null

    if (!shareReport) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `ShareReport with id ${id} not found` }],
      })
    }

    // Create a new ShareReport entry for the resend attempt
    const newShareReport = await schema.create('shareReport', {
      ...shareReport.attrs,
      status: ShareReportStatus.Pending,
      sent_at: null,
      error_message: null,
      created_at: getTodayDateTimeISO(),
      updated_at: getTodayDateTimeISO(),
    })

    return {
      data: {
        id: newShareReport.id,
        type: 'ShareReport',
        attributes: newShareReport.attrs,
      },
    }
  })
}

export function seeds(server: MockServer) {
  server.createList('shareReport', 16)
}
